import React from "react";
import { ArrowRight, Sparkles, CalendarClock } from "lucide-react";
import { Magnetic, TiltCard } from "../lib/Interactive.jsx";
import { useLang } from "../i18n.jsx";

function DeviceMockup({ preview, type }) {
  const wide = type === "tablet";
  return (
    <TiltCard max={6} style={{ borderRadius: wide ? "18px" : "28px" }}>
      <div
        className={`relative overflow-hidden border border-paper/15 bg-ink2 shadow-2xl shadow-black/60 ${
          wide ? "rounded-[18px] aspect-[4/3]" : "rounded-[28px] aspect-[9/19]"
        }`}
      >
        <div className={`${wide ? "px-4 pt-3" : "px-2.5 pt-2.5"}`}>
          <div className="rounded-[6px] border border-paper/10 bg-ink px-2 py-1.5 flex items-center justify-between">
            <span className="text-[7px] text-on-dark/40 truncate">{preview.domain}</span>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: preview.accent }} />
          </div>
        </div>
        <img src={preview.img} alt={preview.name} className={`w-full object-cover object-top ${wide ? "h-[80%]" : "h-[78%]"} opacity-90`} />
        <span
          className="absolute bottom-2.5 right-3 rounded px-2 py-1 text-[8px] font-bold uppercase tracking-wider text-ink"
          style={{ background: preview.accent }}
        >
          {preview.label}
        </span>
      </div>
    </TiltCard>
  );
}

export default function PersonalBrandHero() {
  const { content, waLink } = useLang();
  const { hero } = content;
  const words = (t) => (t || "").split(" ").map((w, i) => (
    <span key={i} className="af-word mr-[0.22em] inline-block">
      {w}
    </span>
  ));

  return (
    <section id="top" className="relative min-h-screen pt-32 pb-14 px-6 overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-acid/[0.05] blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 -left-20 w-[420px] h-[360px] rounded-full bg-acid/[0.03] blur-3xl" />

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center">
        <div>
          <span className="af-hero-badge inline-flex items-center gap-2 rounded-full border border-acid/30 bg-acid/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] text-acid">
            <Sparkles className="w-3.5 h-3.5" /> {hero.badge}
          </span>

          <h1 className="af-hero-h1 mt-6 font-display font-extrabold leading-[1.06] tracking-tight text-[clamp(2.1rem,4.6vw,3.5rem)] text-paper">
            {words(hero.titleTop)}
            <span
              className="af-highlight inline-block rounded-[0.12em] bg-acid px-[0.14em] py-[0.05em] text-ink"
              style={{ transformOrigin: "left center" }}
            >
              {hero.titleHighlight}
            </span>
            <span className="block">{words(hero.titleBottom)}</span>
          </h1>

          <p className="af-hero-sub mt-5 max-w-xl text-paper/75 text-[15px] leading-relaxed">{hero.sub}</p>

          <div className="af-hero-ctas mt-7 flex flex-wrap gap-3">
            <Magnetic>
              <a href="#solusi" data-cursor className="group flex items-center gap-2 rounded-full bg-acid px-6 py-3 font-bold text-ink text-sm hover:bg-paper transition-colors">
                {hero.ctaPrimary}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </Magnetic>
            <Magnetic>
              <a href={waLink} target="_blank" rel="noopener noreferrer" data-cursor className="rounded-full border border-paper/25 px-6 py-3 text-sm text-paper/90 hover:border-paper/60 hover:text-paper transition">
                {hero.ctaSecondary}
              </a>
            </Magnetic>
          </div>

          <div className="af-hero-rate mt-8 border-l-2 border-acid pl-4 text-paper/70 text-[15px] leading-relaxed max-w-md">
            {hero.rateLine}
          </div>

          <div className="mt-8 flex gap-8">
            {hero.stats.map((st) => (
              <div key={st.label} className="af-hero-stat">
                <div className="font-display font-extrabold text-2xl text-acid">{st.num}</div>
                <div className="mt-1 text-xs uppercase tracking-wider text-paper/60 max-w-[130px]">{st.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative h-[540px] hidden md:block">
          <div className="af-ring pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[430px] h-[430px]">
            <div className="af-ring-inner relative h-full w-full rounded-full border border-acid/15">
              <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-acid shadow-[0_0_12px_2px_rgba(212,255,0,0.45)]" />
            </div>
          </div>

          <div className="af-devicewrap af-float absolute top-6 right-4 w-[360px]">
            <DeviceMockup preview={hero.previewTablet} type="tablet" />
          </div>
          <div className="af-devicewrap af-float2 absolute bottom-0 right-32 z-10 w-[210px]">
            <DeviceMockup preview={hero.previewPhone} type="phone" />
          </div>

          <div className="af-chip absolute top-24 -left-6 z-20">
            <div className="flex items-center gap-2 rounded-2xl border border-paper/10 bg-ink2/90 backdrop-blur px-4 py-3 shadow-xl">
              <span className="w-2 h-2 rounded-full bg-acid animate-pulse" />
              <span className="text-[13px] text-paper/85">{hero.bookingChip}</span>
            </div>
          </div>
          <div className="af-chip absolute bottom-24 -right-2 left-auto z-20">
            <div className="flex items-center gap-2 rounded-2xl border border-paper/10 bg-ink2/90 backdrop-blur px-4 py-3 shadow-xl">
              <CalendarClock className="w-4 h-4 text-acid" />
              <span className="text-[13px] text-paper/85">{hero.seoChip}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
